import { getCustomer, getVendor } from "../api/ProcurementClient";
import Customer from "../models/Customer";
import Manufacturer from "../models/Manufacturer";
import Vendor from "../models/Vendor";
import { SessionCatalogs } from "../models/SessionInfo";

const CATALOG_CACHE_KEY = "procurement:session-catalogs:v1";

type CachedCatalogs = SessionCatalogs & { savedAt: number };

export function readCachedSessionCatalogs(): SessionCatalogs | null {
  try {
    const raw = sessionStorage.getItem(CATALOG_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedCatalogs;
    if (
      !Array.isArray(parsed.vendors) ||
      !Array.isArray(parsed.customers) ||
      !Array.isArray(parsed.manufacturers)
    ) {
      return null;
    }
    return {
      vendors: parsed.vendors as Vendor[],
      customers: parsed.customers as Customer[],
      manufacturers: parsed.manufacturers as Manufacturer[],
    };
  } catch {
    return null;
  }
}

export function writeCachedSessionCatalogs(catalogs: SessionCatalogs): void {
  try {
    const payload: CachedCatalogs = {
      ...catalogs,
      savedAt: Date.now(),
    };
    sessionStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

type SetCustomers = (
  value: Customer[] | ((prev: Customer[]) => Customer[])
) => void;

type SetVendors = (
  value: Vendor[] | ((prev: Vendor[]) => Vendor[])
) => void;

function upsertCustomer(prev: Customer[], full: Customer): Customer[] {
  const index = prev.findIndex((c) => c.id === full.id);
  if (index < 0) {
    return [...prev, full];
  }
  const existing = prev[index];
  const incomingContacts = full.contacts ?? [];
  const existingContacts = existing.contacts ?? [];
  const next = prev.slice();
  next[index] = {
    ...existing,
    ...full,
    contacts:
      incomingContacts.length > 0 ? incomingContacts : existingContacts,
  };
  return next;
}

const hydratedCustomerIds = new Set<number>();
const customerHydrateInFlight = new Map<
  number,
  Promise<Customer | undefined>
>();

function customerHasLoadedContacts(customer: Customer | undefined): boolean {
  if (!customer || customer.id <= 1) {
    return false;
  }
  if (hydratedCustomerIds.has(customer.id)) {
    return true;
  }
  return (
    (customer.contacts?.length ?? 0) > 0 && (customer.qcCodes?.length ?? 0) > 0
  );
}

export function rememberCustomerContactsLoaded(customerId: number): void {
  if (customerId > 1) {
    hydratedCustomerIds.add(customerId);
  }
}

function upsertVendor(prev: Vendor[], full: Vendor): Vendor[] {
  const index = prev.findIndex((v) => v.id === full.id);
  if (index < 0) {
    return [...prev, full];
  }
  const existing = prev[index];
  const incomingContacts = full.contacts ?? [];
  const existingContacts = existing.contacts ?? [];
  const next = prev.slice();
  next[index] = {
    ...existing,
    ...full,
    contacts:
      incomingContacts.length > 0 ? incomingContacts : existingContacts,
  };
  return next;
}

const hydratedVendorIds = new Set<number>();
const vendorHydrateInFlight = new Map<number, Promise<Vendor | undefined>>();

function vendorHasLoadedContacts(vendor: Vendor | undefined): boolean {
  if (!vendor || vendor.id <= 0) {
    return false;
  }
  if (hydratedVendorIds.has(vendor.id)) {
    return true;
  }
  return (vendor.contacts?.length ?? 0) > 0;
}

export function rememberVendorContactsLoaded(vendorId: number): void {
  if (vendorId > 0) {
    hydratedVendorIds.add(vendorId);
  }
}

/**
 * Directory-lite session catalogs omit contacts. Keep any contacts already
 * loaded in memory so a catalog refresh does not wipe them.
 */
export function mergeLiteSessionCatalogs(
  prev: SessionCatalogs | null,
  incoming: SessionCatalogs
): SessionCatalogs {
  const prevVendors = prev?.vendors ?? [];
  const prevCustomers = prev?.customers ?? [];
  const prevVendorsById = new Map(prevVendors.map((v) => [v.id, v]));
  const prevCustomersById = new Map(prevCustomers.map((c) => [c.id, c]));
  return {
    vendors: incoming.vendors.map((v) => {
      const old = prevVendorsById.get(v.id);
      const incomingContacts = v.contacts ?? [];
      const oldContacts = old?.contacts ?? [];
      return {
        ...old,
        ...v,
        contacts:
          incomingContacts.length > 0 ? incomingContacts : oldContacts,
      };
    }),
    customers: incoming.customers.map((c) => {
      const old = prevCustomersById.get(c.id);
      const incomingContacts = c.contacts ?? [];
      const oldContacts = old?.contacts ?? [];
      const incomingQcCodes = c.qcCodes ?? [];
      const oldQcCodes = old?.qcCodes ?? [];
      return {
        ...old,
        ...c,
        contacts:
          incomingContacts.length > 0 ? incomingContacts : oldContacts,
        qcCodes: incomingQcCodes.length > 0 ? incomingQcCodes : oldQcCodes,
      };
    }),
    manufacturers: incoming.manufacturers,
  };
}

/**
 * Session catalogs are directory-lite (no contacts). Load the full vendor
 * once so quote editors, contact tables, and vendor RFQ email can resolve them.
 */
export async function ensureVendorHydrated(
  vendorId: number | null | undefined,
  vendors: Vendor[],
  setVendors: SetVendors
): Promise<Vendor | undefined> {
  if (vendorId == null || vendorId <= 0) {
    return undefined;
  }
  const existing = vendors.find((v) => v.id === vendorId);
  if (vendorHasLoadedContacts(existing)) {
    return existing;
  }
  const inFlight = vendorHydrateInFlight.get(vendorId);
  if (inFlight) {
    return inFlight;
  }
  const request = (async () => {
    try {
      const full = await getVendor(vendorId);
      rememberVendorContactsLoaded(vendorId);
      setVendors((prev) => upsertVendor(prev, full));
      return full;
    } catch {
      rememberVendorContactsLoaded(vendorId);
      return existing;
    } finally {
      vendorHydrateInFlight.delete(vendorId);
    }
  })();
  vendorHydrateInFlight.set(vendorId, request);
  return request;
}

/**
 * Session catalogs are directory-lite (no contacts). Load the full customer
 * once so Client/Buyer autocompletes and approve can resolve contacts.
 * Remembers hydration even when the customer has zero contacts so we do not
 * refetch forever; dedupes concurrent loads like ensureVendorHydrated.
 */
export async function ensureCustomerHydrated(
  customerId: number | null | undefined,
  customers: Customer[],
  setCustomers: SetCustomers
): Promise<Customer | undefined> {
  if (customerId == null || customerId <= 1) {
    return undefined;
  }
  const existing = customers.find((c) => c.id === customerId);
  if (customerHasLoadedContacts(existing)) {
    return existing;
  }
  const inFlight = customerHydrateInFlight.get(customerId);
  if (inFlight) {
    return inFlight;
  }
  const request = (async () => {
    try {
      const full = await getCustomer(customerId);
      rememberCustomerContactsLoaded(customerId);
      setCustomers((prev) => upsertCustomer(prev, full));
      return full;
    } catch {
      rememberCustomerContactsLoaded(customerId);
      return existing;
    } finally {
      customerHydrateInFlight.delete(customerId);
    }
  })();
  customerHydrateInFlight.set(customerId, request);
  return request;
}
